import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/**
 * "You may also like" in the cart drawer. Loads Shopify product recommendations for the
 * first cart line and adds with the same CartLinesUpdateEvent the product form uses, so the
 * drawer, the header badge and the free-delivery bar all refresh through the theme's own flow.
 * The element is data-skip-subtree-update, so the drawer morph keeps the loaded list and only
 * syncs data-product-id; we reload after every cart change to refresh the "already in cart" filter.
 * CSS hides the whole block (heading included) while the list has no items; `hidden` does the
 * same for browsers without :has().
 */
class CartRecommendations extends HTMLElement {
  /** Bumped per load so a slower, older response can't overwrite a newer list. */
  #loadId = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        // Macrotask: runs after the drawer's morph (same promise) has synced data-product-id.
        setTimeout(() => this.#load());
      })
      .catch(() => {});
  };

  async #load() {
    if (!this.isConnected) return;
    const list = this.querySelector('.cart-recs__slot');
    if (!list || !this.dataset.productId) return;
    const loadId = ++this.#loadId;
    let html = '';
    try {
      const url = `${window.Shopify?.routes?.root ?? '/'}recommendations/products?product_id=${this.dataset.productId}&limit=8&section_id=cart-recommendations`;
      const response = await fetch(url).then((r) => r.text());
      html = new DOMParser().parseFromString(response, 'text/html').querySelector('.cart-recs__list')?.innerHTML ?? '';
    } catch {}
    if (loadId !== this.#loadId) return;
    list.innerHTML = html;
    this.hidden = !list.querySelector('.cart-recs__item');
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null
    );
    if (!button || button.disabled) return;
    button.disabled = true;
    const item = button.closest('.cart-recs__item');
    this.querySelectorAll('.cart-recs__error').forEach((el) => el.remove());

    const id = button.dataset.variantId;
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const root = window.Shopify?.routes?.root ?? '/';
    /** @type {any} */
    let added = null;
    try {
      added = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections }),
      }).then((r) => r.json());
    } catch {}
    // Only the add itself decides the error message; Shopify returns { status, description } on failure.
    const failed = !added || Boolean(added.status);

    try {
      const cart = await fetch(`${root}cart.js`).then((r) => {
        if (!r.ok) throw new Error(`cart.js ${r.status}`);
        return r.json();
      });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: added?.sections,
          itemCount: 1,
          didError: failed,
          source: 'cart-recommendations',
        },
      });
    } catch (error) {
      deferred.reject(error);
      // The add went through but the cart read failed: redraw the drawer from the sections the add returned.
      if (!failed) {
        for (const [sectionId, html] of Object.entries(added.sections ?? {})) {
          if (html) morphSection(sectionId, /** @type {string} */ (html)).catch(() => {});
        }
        setTimeout(() => this.#load());
      }
    }
    button.disabled = false;

    if (failed && item) {
      const message = document.createElement('p');
      message.className = 'cart-recs__error';
      message.setAttribute('role', 'alert');
      message.textContent = 'Couldn’t add — please try again or contact us';
      item.append(message);
    }
  };
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
